import React from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { createBooking, getBookingByRequestId } from '../services/bookingService';
import { sendBookingConfirmation, sendEmail } from '../services/emailService';
import { getUserLocation, getProvidersByUrgency, formatDistance, URGENCY_MODES } from '../services/locationService';
import './BookingForm.css';

const BOOKING_TIMEOUT_MS = 25000;

function withTimeout(promise, timeoutMs, label) {
    return Promise.race([
        promise,
        new Promise((_, reject) => {
            setTimeout(() => reject(new Error(`${label} timed out. Please try again.`)), timeoutMs);
        })
    ]);
}

const BookingForm = ({ serviceName, workerData, onClose, prefillDescription, urgencyMode: initialUrgency, userLocation: initialLocation }) => {
    const { currentUser } = useAuth();
    const [loading, setLoading] = React.useState(false);
    const [success, setSuccess] = React.useState(false);
    const [error, setError] = React.useState('');

    // Location state
    const [liveLocation, setLiveLocation] = React.useState(initialLocation || null);
    const [locStatus, setLocStatus] = React.useState(initialLocation ? 'success' : 'loading'); // loading | success | error
    const [locError, setLocError] = React.useState('');

    // Urgency & provider matching
    const [urgency, setUrgency] = React.useState(initialUrgency || null);
    const [nearbyProviders, setNearbyProviders] = React.useState([]);
    const [selectedProvider, setSelectedProvider] = React.useState(workerData || null);
    const [loadingProviders, setLoadingProviders] = React.useState(false);

    const [formData, setFormData] = React.useState({
        name: currentUser?.displayName || '',
        phone: '',
        email: currentUser?.email || '',
        address: '',
        date: '',
        time: '',
        description: prefillDescription || ''
    });

    // ── Fetch fresh location when form opens ──
    React.useEffect(() => {
        if (initialLocation) {
            setLiveLocation(initialLocation);
            setLocStatus('success');
            return;
        }

        setLocStatus('loading');
        getUserLocation()
            .then((loc) => {
                setLiveLocation(loc);
                setLocStatus('success');
            })
            .catch((err) => {
                setLocError(err.message);
                setLocStatus('error');
            });
    }, [initialLocation]);

    // ── Fetch & sort nearby providers when location + urgency are ready ──
    React.useEffect(() => {
        if (!liveLocation || !urgency || workerData) return; // skip if specific worker already chosen

        const fetchAndSort = async () => {
            setLoadingProviders(true);
            try {
                const serviceSlug = serviceName ? serviceName.toLowerCase().replace(/\s+/g, '-') : '';
                const q1 = query(collection(db, 'serviceProviders'), where('serviceCategory', '==', serviceSlug));
                const q2 = query(collection(db, 'serviceProviders'), where('serviceCategory', '==', serviceName));
                const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

                const seenIds = new Set();
                const allPros = [];
                [...snap1.docs, ...snap2.docs].forEach(d => {
                    if (!seenIds.has(d.id)) { seenIds.add(d.id); allPros.push({ id: d.id, ...d.data() }); }
                });

                const sorted = getProvidersByUrgency(allPros, liveLocation.latitude, liveLocation.longitude, urgency);
                setNearbyProviders(sorted);

                // Auto-select the nearest provider
                if (sorted.length > 0 && sorted[0].distance !== Infinity) {
                    setSelectedProvider(sorted[0]);
                }
            } catch (e) {
                console.error('Failed to fetch nearby providers:', e);
            } finally {
                setLoadingProviders(false);
            }
        };

        fetchAndSort();
    }, [liveLocation, urgency, serviceName, workerData]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const triggerEmail = async (task) => {
        try {
            await task();
        } catch (emailError) {
            console.error('Email trigger failed:', emailError);
        }
    };

    const resolveProviderEmail = async (provider) => {
        const directEmail = (provider?.email || '').trim();
        if (directEmail) return directEmail;

        const providerId = provider?.id || provider?.uid;
        if (!providerId) return '';

        try {
            const providerSnap = await getDoc(doc(db, 'serviceProviders', providerId));
            if (!providerSnap.exists()) return '';

            const providerData = providerSnap.data() || {};
            return (providerData.email || '').trim();
        } catch (error) {
            console.warn('Could not resolve provider email from profile:', error);
            return '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (!currentUser) {
                throw new Error('Please login before submitting a booking.');
            }

            // Immediate: assign to nearest provider. Flexible: leave open for all.
            const isImmediate = urgency === 'immediate';
            const bookingRequestId = `${currentUser.uid}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

            let booking;

            try {
                booking = await withTimeout(createBooking({
                    customerId: currentUser?.uid || 'guest-' + Date.now(),
                    customerName: formData.name,
                    customerEmail: formData.email,
                    customerPhone: formData.phone,
                    serviceName: serviceName || 'General Service',
                    serviceCategory: serviceName ? serviceName.toLowerCase().replace(/\s+/g, '-') : 'general',
                    description: formData.description,
                    address: formData.address,
                    preferredDate: formData.date,
                    preferredTime: formData.time,
                    providerId: isImmediate ? (selectedProvider?.id || null) : null,
                    providerName: isImmediate ? (selectedProvider?.name || null) : null,
                    visitFee: selectedProvider?.visitFee || 0,
                    hourlyRate: selectedProvider?.hourlyRate || 0,
                    estimatedPrice: selectedProvider?.visitFee || selectedProvider?.hourlyRate || null,
                    urgencyMode: urgency || null,
                    customerLatitude: liveLocation?.latitude || null,
                    customerLongitude: liveLocation?.longitude || null,
                    bookingRequestId
                }), BOOKING_TIMEOUT_MS, 'Booking request');
            } catch (submitErr) {
                const submitMsg = (submitErr?.message || '').toLowerCase();
                if (!submitMsg.includes('timed out')) {
                    throw submitErr;
                }

                // If write timed out on client, check if booking was still saved.
                const recovered = await getBookingByRequestId(bookingRequestId);
                if (!recovered) {
                    throw submitErr;
                }

                booking = recovered;
            }

            // ── Booking created successfully: show success immediately ──
            setSuccess(true);
            setTimeout(() => {
                onClose && onClose();
            }, 2000);

            // ── Emails: fully non-blocking, never affect the success state ──
            void (async () => {
                try {
                    if (formData.email) {
                        await triggerEmail(() => sendBookingConfirmation(formData.email, {
                            customerName: formData.name,
                            bookingId: booking.id,
                            serviceName: serviceName || 'General Service',
                            address: formData.address,
                            preferredDate: formData.date,
                            preferredTime: formData.time,
                        }));
                    }
                } catch (emailErr) {
                    console.warn('Non-fatal: customer confirmation email failed:', emailErr);
                }

                if (isImmediate) {
                    try {
                        const providerEmail = await resolveProviderEmail(selectedProvider);
                        if (providerEmail) {
                            await triggerEmail(() => sendEmail('provider-notification', providerEmail, {
                                providerName: selectedProvider.name,
                                bookingId: booking.id,
                                customerName: formData.name,
                                serviceName: serviceName || 'General Service',
                                address: formData.address,
                                urgencyMode: urgency || null,
                            }));
                        } else {
                            console.warn(`Skipping provider notification for booking ${booking.id}: provider email not found`);
                        }
                    } catch (emailErr) {
                        console.warn('Non-fatal: provider notification email failed:', emailErr);
                    }
                }
            })();

        } catch (err) {
            console.error('Booking failed:', err?.code || err?.message, err);
            const code = err?.code || '';
            const msg = (err?.message || '').toLowerCase();

            if (code === 'unauthenticated' || msg.includes('auth') || msg.includes('login')) {
                setError('Your session expired. Please login again and retry.');
            } else if (code === 'permission-denied' || msg.includes('permission')) {
                setError('Permission denied while creating booking. Please login again and retry.');
            } else if (code === 'unavailable' || msg.includes('network') || msg.includes('timed out')) {
                setError('Network issue while submitting booking. Please check your internet and try again.');
            } else {
                setError(err?.message || 'Failed to submit booking. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="booking-form-container">
            <div className="booking-form-content">
                <button className="close-btn" onClick={onClose}>✕</button>

                {success ? (
                    <div className="booking-success">
                        <span className="success-icon">✅</span>
                        <h2>Booking Submitted!</h2>
                        <p>Your service request has been sent to our professionals. You'll be notified once a provider accepts your request.</p>
                        <p className="success-hint">Check "My Bookings" to track your request status.</p>
                    </div>
                ) : (
                    <>
                        <h2>Book {serviceName || 'Service'}</h2>
                        <p className="form-subtitle">Fill in your details and we'll connect you with the best professional</p>

                        {error && <div className="booking-error">{error}</div>}
                        {!currentUser && (
                            <div className="booking-warning">
                                ⚠️ You're not logged in. <a href="/login">Login</a> to track your bookings.
                            </div>
                        )}

                        {/* ── Location Status ── */}
                        <div className="booking-location-bar">
                            {locStatus === 'loading' && (
                                <span className="loc-detecting">
                                    <span className="loc-dot-pulse"></span> Detecting your location…
                                </span>
                            )}
                            {locStatus === 'success' && (
                                <span className="loc-success">📍 Location detected</span>
                            )}
                            {locStatus === 'error' && (
                                <span className="loc-fail">⚠️ {locError || 'Could not get location'}</span>
                            )}
                        </div>

                        {/* ── Urgency Picker (only if no worker pre-selected) ── */}
                        {!workerData && (
                            <div className="booking-urgency-picker">
                                <label className="urgency-label">How soon do you need service?</label>
                                <div className="urgency-options">
                                    <button
                                        type="button"
                                        className={`urgency-opt urgency-opt--immediate ${urgency === URGENCY_MODES.IMMEDIATE ? 'active' : ''}`}
                                        onClick={() => setUrgency(URGENCY_MODES.IMMEDIATE)}
                                    >
                                        ⚡ Immediately
                                    </button>
                                    <button
                                        type="button"
                                        className={`urgency-opt urgency-opt--flexible ${urgency === URGENCY_MODES.FLEXIBLE ? 'active' : ''}`}
                                        onClick={() => setUrgency(URGENCY_MODES.FLEXIBLE)}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '5px', verticalAlign: 'middle' }}>
                                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="currentColor" />
                                            <circle cx="12" cy="9" r="2.5" fill="#fff" />
                                        </svg>
                                        I can wait
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── Matched Provider ── */}
                        {selectedProvider && (
                            <div className="matched-provider-card">
                                <div className="matched-provider-info">
                                    <span className="matched-avatar">👤</span>
                                    <div>
                                        <strong>{selectedProvider.name}</strong>
                                        <span className="matched-meta">
                                            {selectedProvider.rating ? `⭐ ${selectedProvider.rating.toFixed ? selectedProvider.rating.toFixed(1) : selectedProvider.rating}` : ''}
                                            {selectedProvider.distance && selectedProvider.distance !== Infinity
                                                ? ` · ${formatDistance(selectedProvider.distance)} away`
                                                : ''}
                                            {selectedProvider.visitFee ? ` · ₹${selectedProvider.visitFee} visit fee` : ''}
                                            {selectedProvider.hourlyRate ? ` + ₹${selectedProvider.hourlyRate}/hr` : ''}
                                        </span>
                                    </div>
                                </div>
                                {!workerData && nearbyProviders.length > 1 && (
                                    <button
                                        type="button"
                                        className="change-provider-btn"
                                        onClick={() => setSelectedProvider(null)}
                                    >
                                        Change
                                    </button>
                                )}
                            </div>
                        )}

                        {/* ── Provider List (if user clicks Change) ── */}
                        {!workerData && !selectedProvider && nearbyProviders.length > 0 && (
                            <div className="provider-picker-list">
                                <label className="urgency-label">Select a professional:</label>
                                {loadingProviders ? (
                                    <p className="picker-loading">Finding nearby professionals…</p>
                                ) : (
                                    nearbyProviders.slice(0, 5).map((p) => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            className={`provider-pick-item ${selectedProvider?.id === p.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedProvider(p)}
                                        >
                                            <span className="pick-name">{p.name}</span>
                                            <span className="pick-details">
                                                {p.rating ? `⭐ ${p.rating.toFixed ? p.rating.toFixed(1) : p.rating}` : ''}
                                                {p.distance && p.distance !== Infinity ? ` · ${formatDistance(p.distance)}` : ''}
                                                {p.visitFee ? ` · ₹${p.visitFee} visit` : ''}
                                                {p.hourlyRate ? ` + ₹${p.hourlyRate}/hr` : ''}
                                            </span>
                                        </button>
                                    ))
                                )}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="name">Full Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter your name"
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="phone">Phone Number *</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        required
                                        placeholder="+91 XXXXX XXXXX"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">Email *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        placeholder="your@email.com"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="address">Service Address *</label>
                                <input
                                    type="text"
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter complete address"
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="date">Preferred Date *</label>
                                    <input
                                        type="date"
                                        id="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="time">Preferred Time *</label>
                                    <input
                                        type="time"
                                        id="time"
                                        name="time"
                                        value={formData.time}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Service Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="4"
                                    placeholder="Describe the work you need done..."
                                ></textarea>
                            </div>

                            <button type="submit" className="btn btn-primary btn-lg submit-btn" disabled={loading}>
                                {loading ? '⏳ Submitting...' : 'Submit Booking Request'}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default BookingForm;
